import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SoftwareApplicationSchema, BreadcrumbSchema, HowToSchema, OrganizationSchema } from '@/components/SEOSchema';
import { 
  FileText, Calculator, Check, ChevronDown, Shield, Zap, Euro, 
  Plus, Trash2, Download, AlertCircle, Info, Building2, User, Receipt,
  Clock, Star, ArrowRight, Sparkles, TrendingUp, Users, Mail, Save,
  CheckCircle2, Copy, X, Globe, QrCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import Footer from '@/components/Footer';
import SeoClusterLinks from '@/components/SeoClusterLinks';
import { useTracking, useScrollTracking, useSectionTracking } from '@/hooks/useTracking';
import { useCountryPageSEO } from '@/hooks/useSEO';
import {
  TAX_RATES, DEFAULT_TAX_RATE, CURRENCY, KLEINUNTERNEHMER_TEXT,
  TAX_ID_LABELS, UNITS, formatCurrency, formatNumber,
  saveSenderData, loadSenderData
} from '@/utils/invoiceGeneratorConfig';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Live Counter Component (Social Proof)
const LiveCounter = () => {
  const [count, setCount] = useState(1247);
  
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setCount(c => c + 1);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <span className="flex h-2 w-2 relative">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
      </span>
      <span><strong>{count.toLocaleString('de-DE')}</strong> Rechnungen heute erstellt</span>
    </div>
  );
};

// Trust Badges Component (simplified)
const TrustBadges = ({ cfg }) => (
  <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 py-3 text-sm text-gray-600" data-testid="generator-trust-line">
    <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" /> Ohne Anmeldung</span>
    <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-emerald-600" /> Daten bleiben im Browser</span>
    <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-600" /> {cfg.trustBadgeCompliance}</span>
    <span className="flex items-center gap-1.5"><Download className="h-4 w-4 text-emerald-600" /> PDF sofort</span>
  </div>
);

// Floating CTA (Sticky)
const FloatingCTA = ({ onGenerate, isValid }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40 md:hidden">
    <Button 
      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
      onClick={onGenerate}
    >
      <Download className="h-5 w-5 mr-2" />
      PDF erstellen
    </Button>
  </div>
);

// Progress Indicator
const ProgressIndicator = ({ step }) => {
  const steps = ['Absender', 'Empfänger', 'Details', 'Positionen'];
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-2 ${i <= step ? 'text-emerald-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              i < step ? 'bg-emerald-600 text-white' : 
              i === step ? 'bg-emerald-100 text-emerald-600 border-2 border-emerald-600' : 
              'bg-gray-100 text-gray-400'
            }`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className="hidden sm:inline text-sm font-medium">{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-emerald-600' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

// Conversion Hub Modal - The most important screen for conversions
const ConversionHubModal = ({ isOpen, onClose, region, pdfDoc, fileName, trackEvent, invoiceData, onSaveAndSignup }) => {
  const [email, setEmail] = useState('');
  const [emailConsent, setEmailConsent] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  if (!isOpen) return null;
  
  const handleDownloadPDF = () => {
    if (pdfDoc) {
      pdfDoc.save(fileName);
      trackEvent('invoice_pdf_downloaded', region);
    }
  };
  
  const handleSaveAndSignup = () => {
    // Save data to localStorage and navigate to signup
    if (onSaveAndSignup) {
      onSaveAndSignup();
    }
    trackEvent('invoice_signup_clicked', region);
  };
  
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setEmailError('Bitte E-Mail eingeben');
      return;
    }
    if (!emailConsent) {
      setEmailError('Bitte bestätige die Checkbox');
      return;
    }
    
    setEmailLoading(true);
    setEmailError('');
    
    try {
      // Call backend API to capture lead and send email
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/invoice-generator/capture-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          region: region,
          consent_marketing: emailConsent,
          invoice_data: invoiceData
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Fehler beim Senden');
      }
      
      // Track the lead capture event
      trackEvent('invoice_email_captured', region);
      
      setEmailSent(true);
      
      // Auto-download after email capture
      if (pdfDoc) {
        pdfDoc.save(fileName);
      }
    } catch (err) {
      console.error('Email capture error:', err);
      setEmailError(err.message || 'Fehler beim Senden. Bitte versuche es erneut.');
    } finally {
      setEmailLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl animate-in fade-in zoom-in duration-300 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-emerald-500 to-teal-500 p-6 rounded-t-2xl">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Rechnung erstellt! 🎉</h2>
              <p className="text-emerald-100">Wähle, wie du deine Rechnung erhalten möchtest</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Option A: Primary - PDF Download */}
          <div className="border-2 border-emerald-200 bg-emerald-50/50 rounded-xl p-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Download className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">PDF herunterladen</h3>
                <p className="text-sm text-gray-600 mb-3">Lade deine Rechnung sofort als PDF herunter</p>
                <button
                  onClick={handleDownloadPDF}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 px-4 rounded-lg font-semibold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  PDF herunterladen
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  💡 Tipp: Speichern & wiederverwenden mit ivoliq
                </p>
              </div>
            </div>
          </div>
          
          {/* Option B: Email Lead Capture */}
          <div className="border border-gray-200 rounded-xl p-4 hover:border-emerald-200 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Kopie per E-Mail</h3>
                <p className="text-sm text-gray-600 mb-3">Erhalte eine Bestätigung in dein Postfach</p>
                
                {emailSent ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-green-700 font-medium">E-Mail gesendet!</span>
                  </div>
                ) : (
                  <form onSubmit={handleEmailSubmit} className="space-y-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="deine@email.de"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailConsent}
                        onChange={(e) => setEmailConsent(e.target.checked)}
                        className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-xs text-gray-600">
                        Ich möchte die Bestätigung per E-Mail erhalten und stimme zu, gelegentlich Produktupdates zu erhalten (jederzeit abbestellbar).
                      </span>
                    </label>
                    {emailError && <p className="text-red-500 text-xs">{emailError}</p>}
                    <button
                      type="submit"
                      disabled={emailLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {emailLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4" />
                          Per E-Mail senden
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
          
          {/* Option C: Account Creation - Hard Value */}
          <div className="border border-gray-200 rounded-xl p-4 bg-gradient-to-r from-amber-50/50 to-orange-50/50 hover:border-amber-200 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Save className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Speichern & später bearbeiten</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Mit einem ivoliq Konto kannst du Kunden speichern, Nummernkreise automatisieren und Rechnungen in Sekunden wieder erstellen.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {[
                    'Kunden speichern',
                    'Auto-Nummernkreise',
                    'Rechnungen wiederverwenden',
                    'Zeiterfassung inklusive'
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <Check className="h-3 w-3 text-amber-500" />
                      {feature}
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleSaveAndSignup}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-3 px-4 rounded-lg font-semibold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Kostenlos starten
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Free Plan ohne Kreditkarte • <Link to="/pricing" className="text-emerald-600 hover:underline">Mehr Features ansehen</Link>
                </p>
                <p className="text-xs text-emerald-600 mt-1 text-center flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Deine Rechnungsdaten werden übernommen
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full text-gray-500 hover:text-gray-700 text-center py-2 text-sm"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

// Feature Highlight Box
const FeatureHighlight = ({ icon: Icon, title, description, cta, region }) => (
  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-start gap-4">
    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon className="h-5 w-5 text-amber-600" />
    </div>
    <div className="flex-1">
      <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600 mb-2">{description}</p>
      <Link 
        to={`/signup?ref=invoice-generator-${region}-${cta.toLowerCase().replace(/\s/g, '-')}`}
        className="text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1"
      >
        {cta} <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  </div>
);

export default function InvoiceGeneratorTemplate({ cfg }) {
  const region = cfg.region;
  const country = cfg.country;
  
  // SEO configuration with self-referential canonical
  useCountryPageSEO(cfg.seoPageKey);
  
  // Tracking
  const { trackCTAClick, trackFAQOpen } = useTracking();
  useScrollTracking();
  
  // Navigation
  const navigate = useNavigate();
  
  // Section refs
  const heroRef = useRef(null);
  const formRef = useRef(null);
  const faqRef = useRef(null);
  const warumRef = useRef(null);
  const pflichtangabenRef = useRef(null);
  const upgradeRef = useRef(null);
  useSectionTracking('hero', heroRef);
  useSectionTracking('form', formRef);
  useSectionTracking('faq', faqRef);
  useSectionTracking('warum-generator', warumRef);
  useSectionTracking('pflichtangaben', pflichtangabenRef);
  useSectionTracking('upgrade-ivoliq', upgradeRef);
  
  // SEO Meta Tags
  useEffect(() => {
    document.title = cfg.seoTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', cfg.seoDescription);
    }
  }, [cfg]);
  
  // State
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [isKleinunternehmer, setIsKleinunternehmer] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPDF, setGeneratedPDF] = useState(null);
  const [generatedFileName, setGeneratedFileName] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState(null);
  
  // Form data
  const [sender, setSender] = useState({
    name: '',
    street: '',
    ...(cfg.hasSwissQR ? { streetNumber: '' } : {}),
    zip: '',
    city: '',
    country: cfg.defaultCountryName,
    email: '',
    phone: '',
    website: '',
    vatId: '',
    taxNumber: '',
    iban: '',
    bic: '',
    bank: '',
  });
  
  const [recipient, setRecipient] = useState({
    name: '',
    street: '',
    ...(cfg.hasSwissQR ? { streetNumber: '' } : {}),
    zip: '',
    city: '',
    country: cfg.defaultCountryName,
    vatId: '',
    customerNumber: '',
  });
  
  const [invoice, setInvoice] = useState({
    number: '',
    date: new Date().toISOString().split('T')[0],
    serviceStartDate: new Date().toISOString().split('T')[0],
    serviceEndDate: '',
    dueDate: '',
    poNumber: '',
    ...(cfg.hasSwissQR ? { reference: '' } : {}),
  });
  
  const [items, setItems] = useState([{
    id: 1,
    description: '',
    quantity: 1,
    unit: 'Stunde',
    price: '',
    taxRate: DEFAULT_TAX_RATE[country],
  }]);
  
  // Notes/custom text for invoice
  const [notes, setNotes] = useState('');
  
  // Keys for localStorage
  const INVOICE_DATA_KEY = 'ivoliq_invoice_draft';
  const INVOICE_DATA_EXPIRY_KEY = 'ivoliq_invoice_draft_expiry';
  
  // Save all invoice data to localStorage (for restore after signup)
  const saveInvoiceDataToStorage = () => {
    const invoiceData = {
      sender,
      recipient,
      invoice,
      items,
      notes,
      isKleinunternehmer,
      region,
      savedAt: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(INVOICE_DATA_KEY, JSON.stringify(invoiceData));
      // Set expiry to 24 hours
      const expiry = new Date();
      expiry.setHours(expiry.getHours() + 24);
      localStorage.setItem(INVOICE_DATA_EXPIRY_KEY, expiry.toISOString());
      console.log('📝 Invoice data saved to localStorage');
      return true;
    } catch (e) {
      console.error('Failed to save invoice data:', e);
      return false;
    }
  };
  
  // Load invoice data from localStorage (after signup/login)
  const loadInvoiceDataFromStorage = () => {
    try {
      const expiryStr = localStorage.getItem(INVOICE_DATA_EXPIRY_KEY);
      if (expiryStr) {
        const expiry = new Date(expiryStr);
        if (new Date() > expiry) {
          // Data expired, clear it
          clearInvoiceDataFromStorage();
          return null;
        }
      }
      
      const data = localStorage.getItem(INVOICE_DATA_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      console.error('Failed to load invoice data:', e);
    }
    return null;
  };
  
  // Clear invoice data from localStorage
  const clearInvoiceDataFromStorage = () => {
    try {
      localStorage.removeItem(INVOICE_DATA_KEY);
      localStorage.removeItem(INVOICE_DATA_EXPIRY_KEY);
    } catch (e) {
      console.error('Failed to clear invoice data:', e);
    }
  };
  
  // Check URL params for restore flag and load saved data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldRestore = urlParams.get('restore') === 'true';
    
    if (shouldRestore) {
      const savedData = loadInvoiceDataFromStorage();
      if (savedData) {
        console.log('🔄 Restoring invoice data from localStorage');
        
        // Restore all data
        if (savedData.sender) setSender(prev => ({ ...prev, ...savedData.sender }));
        if (savedData.recipient) setRecipient(prev => ({ ...prev, ...savedData.recipient }));
        if (savedData.invoice) setInvoice(prev => ({ ...prev, ...savedData.invoice }));
        if (savedData.items && savedData.items.length > 0) setItems(savedData.items);
        if (savedData.notes) setNotes(savedData.notes);
        if (typeof savedData.isKleinunternehmer === 'boolean') setIsKleinunternehmer(savedData.isKleinunternehmer);
        
        // Clear URL params after restore
        window.history.replaceState({}, '', window.location.pathname);
        
        // Show success message
        setTimeout(() => {
          alert('✅ Deine Rechnungsdaten wurden wiederhergestellt!');
        }, 500);
      }
    }
  }, []);
  
  // Load saved sender data on mount (existing functionality)
  useEffect(() => {
    const saved = loadSenderData();
    if (saved) {
      setSender(prev => ({ ...prev, ...saved }));
    }
  }, []);
  
  // Update current step based on form progress
  useEffect(() => {
    if (sender.name && sender.street && sender.email) {
      if (recipient.name && recipient.street) {
        if (invoice.number && invoice.date) {
          setCurrentStep(3);
        } else {
          setCurrentStep(2);
        }
      } else {
        setCurrentStep(1);
      }
    } else {
      setCurrentStep(0);
    }
  }, [sender, recipient, invoice]);
  
  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    const taxBreakdown = {};
    
    items.forEach(item => {
      const price = parseFloat(item.price) || 0;
      const lineTotal = item.quantity * price;
      subtotal += lineTotal;
      
      const taxRate = isKleinunternehmer ? 0 : item.taxRate;
      if (!taxBreakdown[taxRate]) {
        taxBreakdown[taxRate] = 0;
      }
      taxBreakdown[taxRate] += lineTotal * (taxRate / 100);
    });
    
    const totalTax = Object.values(taxBreakdown).reduce((a, b) => a + b, 0);
    const total = subtotal + totalTax;
    
    return { subtotal, taxBreakdown, totalTax, total };
  };
  
  const totals = calculateTotals();
  
  // Handlers
  const handleSenderChange = (field, value) => {
    setSender(prev => {
      const updated = { ...prev, [field]: value };
      saveSenderData(updated);
      return updated;
    });
  };
  
  const handleRecipientChange = (field, value) => {
    setRecipient(prev => ({ ...prev, [field]: value }));
  };
  
  const handleInvoiceChange = (field, value) => {
    setInvoice(prev => ({ ...prev, [field]: value }));
  };
  
  const handleItemChange = (id, field, value) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };
  
  const handlePriceChange = (id, value) => {
    // Allow empty string or valid number input
    const cleanValue = value.replace(/[^0-9.,]/g, '').replace(',', '.');
    handleItemChange(id, 'price', cleanValue);
  };
  
  const addItem = () => {
    if (items.length >= 20) {
      alert('Maximal 20 Positionen. Registriere dich für unbegrenzte Positionen!');
      return;
    }
    setItems(prev => [...prev, {
      id: Date.now(),
      description: '',
      quantity: 1,
      unit: 'Stunde',
      price: '',
      taxRate: DEFAULT_TAX_RATE[country],
    }]);
  };
  
  const removeItem = (id) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(item => item.id !== id));
  };
  
  // Handle "Save & Signup" - saves data and navigates to signup
  const handleSaveAndSignup = () => {
    saveInvoiceDataToStorage();
    // Navigate to signup with restore parameter
    navigate(`/signup?ref=invoice-generator-${region}-save&restore=true&return=${cfg.returnPath}`);
  };
  
  // Format service period for display
  const getServicePeriodText = () => {
    const start = invoice.serviceStartDate;
    const end = invoice.serviceEndDate;
    
    if (!start) return '';
    
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    
    if (end && end !== start) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    return formatDate(start);
  };
  
  // Validation
  const validate = () => {
    const newErrors = {};
    
    // Sender validation
    if (!sender.name.trim()) newErrors['sender.name'] = 'Name ist erforderlich';
    if (!sender.street.trim()) newErrors['sender.street'] = 'Straße ist erforderlich';
    if (!sender.zip.trim()) newErrors['sender.zip'] = 'PLZ ist erforderlich';
    if (!sender.city.trim()) newErrors['sender.city'] = 'Ort ist erforderlich';
    if (!sender.email.trim()) newErrors['sender.email'] = 'E-Mail ist erforderlich';
    
    // Swiss QR-Bill IBAN validation
    if (cfg.hasSwissQR && sender.iban && totals.total > 0) {
      const ibanPattern = /^(CH|LI)[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/;
      if (!ibanPattern.test(sender.iban.replace(/\s/g, ''))) {
        newErrors['sender.iban'] = 'Ungültige IBAN (CH oder LI erforderlich)';
      }
    }
    
    // Recipient validation
    if (!recipient.name.trim()) newErrors['recipient.name'] = 'Kundenname ist erforderlich';
    if (!recipient.street.trim()) newErrors['recipient.street'] = 'Straße ist erforderlich';
    if (!recipient.zip.trim()) newErrors['recipient.zip'] = 'PLZ ist erforderlich';
    if (!recipient.city.trim()) newErrors['recipient.city'] = 'Ort ist erforderlich';
    
    // Invoice validation
    if (!invoice.number.trim()) newErrors['invoice.number'] = 'Rechnungsnummer ist erforderlich';
    if (!invoice.date) newErrors['invoice.date'] = 'Rechnungsdatum ist erforderlich';
    if (!invoice.serviceStartDate) newErrors['invoice.serviceStartDate'] = 'Leistungsdatum ist erforderlich';
    
    // Items validation
    items.forEach((item, index) => {
      if (!item.description.trim()) newErrors[`items.${index}.description`] = 'Beschreibung erforderlich';
      const price = parseFloat(item.price) || 0;
      if (price <= 0) newErrors[`items.${index}.price`] = 'Preis erforderlich';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Swiss QR-Bill generation (CH only)
  const generateSwissQR = async (invoiceTotal, overrideIban = null) => {
    if (!cfg.hasSwissQR) return null;
    const ibanToUse = overrideIban || sender.iban;
    if (!ibanToUse || !invoiceTotal || invoiceTotal <= 0) return null;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/swiss-qr/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          iban: ibanToUse.replace(/\s/g, ''),
          amount: invoiceTotal,
          currency: 'CHF',
          creditor_name: sender.name,
          creditor_street: sender.street || '',
          creditor_street_number: sender.streetNumber || '',
          creditor_postal_code: sender.zip,
          creditor_city: sender.city,
          creditor_country: 'CH',
          debtor_name: recipient.name || '',
          debtor_street: recipient.street || '',
          debtor_street_number: recipient.streetNumber || '',
          debtor_postal_code: recipient.zip || '',
          debtor_city: recipient.city || '',
          debtor_country: 'CH',
          reference: invoice.reference || '',
          additional_info: `Rechnung ${invoice.number}`
        })
      });
      if (!response.ok) throw new Error('QR-Code generation failed');
      const data = await response.json();
      return data.qr_code;
    } catch (error) {
      console.error('Swiss QR generation error:', error);
      return null;
    }
  };
  
  // Generate PDF
  const generatePDF = async () => {
    if (!validate()) {
      const firstError = document.querySelector('.border-red-500');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setIsGenerating(true);
    
    // Small delay for UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      // Swiss QR-Bill: Generate QR code before PDF creation
      let qrCodeBase64 = null;
      let senderData = { ...sender };
      if (cfg.hasSwissQR) {
        const ibanInput = document.querySelector('input[placeholder*="CH93"]');
        const currentIban = ibanInput ? ibanInput.value : sender.iban;
        const ibanForQR = currentIban || sender.iban;
        if (ibanForQR && totals.total > 0) {
          try {
            qrCodeBase64 = await generateSwissQR(totals.total, ibanForQR);
            if (qrCodeBase64) setQrCodeImage(qrCodeBase64);
          } catch (qrError) {
            console.error('QR generation error:', qrError);
          }
        }
        senderData = { ...sender, iban: ibanForQR };
      }

      // A4 Format: 210mm x 297mm
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      
      // Professional margins - more balanced and centered
      const marginLeft = 25;
      const marginRight = 25;
      const marginTop = 30; // Larger top margin for professional look
      const contentWidth = pageWidth - marginLeft - marginRight; // 160mm
      const rightEdge = pageWidth - marginRight; // 185mm
      
      let y = marginTop;
      
      // === HEADER: Sender address line (small, gray, elegant) ===
      doc.setFontSize(8);
      doc.setTextColor(120);
      const senderLine = cfg.hasSwissQR 
        ? `${sender.name}  •  ${sender.street} ${sender.streetNumber || ''}  •  ${sender.zip} ${sender.city}`
        : `${sender.name}  •  ${sender.street}  •  ${sender.zip} ${sender.city}`;
      
      // Check if header line needs wrapping
      const headerMaxWidth = contentWidth; // Use full content width
      const headerLineWidth = doc.getTextWidth(senderLine);
      
      if (headerLineWidth > headerMaxWidth) {
        // Split into multiple lines using word wrap
        const words = senderLine.split(' ');
        let currentLine = '';
        
        words.forEach((word) => {
          const testLine = currentLine ? currentLine + ' ' + word : word;
          const testWidth = doc.getTextWidth(testLine);
          
          if (testWidth > headerMaxWidth && currentLine) {
            doc.text(currentLine, marginLeft, y);
            y += 4;
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        });
        
        if (currentLine) {
          doc.text(currentLine, marginLeft, y);
          y += 4;
        }
        y += 8; // Space after wrapped header
      } else {
        doc.text(senderLine, marginLeft, y);
        y += 15; // More space after single-line header
      }
      
      // === ADDRESS BLOCKS: Side by side on same height ===
      const addressStartY = y;
      
      // Recipient block (LEFT side - standard in Germany)
      doc.setFontSize(10);
      doc.setTextColor(60); // Slightly softer black
      doc.setFont(undefined, 'bold');
      doc.text(recipient.name, marginLeft, y);
      doc.setFont(undefined, 'normal');
      y += 5;
      doc.text(recipient.street, marginLeft, y);
      y += 5;
      doc.text(`${recipient.zip} ${recipient.city}`, marginLeft, y);
      y += 5;
      if (recipient.country && recipient.country !== cfg.defaultCountryName) {
        doc.text(recipient.country, marginLeft, y);
        y += 5;
      }
      if (recipient.vatId) {
        doc.text(`USt-IdNr.: ${recipient.vatId}`, marginLeft, y);
        y += 5;
      }
      const recipientEndY = y;
      
      // Sender block (RIGHT side - LEFT-ALIGNED block, positioned so longest line ends at right edge)
      const senderLines = [];
      senderLines.push({ text: sender.name, bold: true, size: 10 });
      senderLines.push({ text: sender.street, bold: false, size: 10 });
      senderLines.push({ text: `${sender.zip} ${sender.city}`, bold: false, size: 10 });
      if (sender.email) senderLines.push({ text: sender.email, bold: false, size: 10 });
      if (sender.phone) senderLines.push({ text: sender.phone, bold: false, size: 10 });
      if (sender.vatId) senderLines.push({ text: `USt-IdNr.: ${sender.vatId}`, bold: false, size: 9 });
      if (sender.taxNumber) senderLines.push({ text: `Steuernr.: ${sender.taxNumber}`, bold: false, size: 9 });
      
      // Find the width of each sender line and determine the longest
      let maxSenderWidth = 0;
      senderLines.forEach(line => {
        doc.setFontSize(line.size);
        doc.setFont(undefined, line.bold ? 'bold' : 'normal');
        const width = doc.getTextWidth(line.text);
        if (width > maxSenderWidth) maxSenderWidth = width;
      });
      
      // Also calculate the width of the invoice date line (will be rendered later but need width now)
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      const dateText = `Rechnungsdatum: ${new Date(invoice.date).toLocaleDateString(cfg.locale)}`;
      const dateWidth = doc.getTextWidth(dateText);
      
      // The right block X position is based on the LONGER of sender or date
      const maxRightBlockWidth = Math.max(maxSenderWidth, dateWidth);
      
      // Calculate starting X position so longest line ends at right edge
      // BUT ensure minimum X position to prevent overlap with recipient block
      const minRightBlockX = 110; // Minimum X position (~center of page)
      const maxAllowedWidth = rightEdge - minRightBlockX; // Maximum width (~75mm)
      
      let rightBlockX;
      
      if (maxRightBlockWidth > maxAllowedWidth) {
        // Text is too long - position at minimum X
        rightBlockX = minRightBlockX;
      } else {
        // Normal case - position so longest line ends at right edge
        rightBlockX = rightEdge - maxRightBlockWidth;
      }
      
      // Render all sender lines with word wrapping for long text
      let senderY = addressStartY;
      doc.setTextColor(60);
      senderLines.forEach((line) => {
        doc.setFontSize(line.size);
        doc.setFont(undefined, line.bold ? 'bold' : 'normal');
        
        const lineWidth = doc.getTextWidth(line.text);
        if (lineWidth > maxAllowedWidth) {
          // Text is too long - wrap it
          const words = line.text.split(' ');
          let currentLine = '';
          
          words.forEach((word, idx) => {
            const testLine = currentLine ? currentLine + ' ' + word : word;
            const testWidth = doc.getTextWidth(testLine);
            
            if (testWidth > maxAllowedWidth && currentLine) {
              // Current line is full, render it and start new line
              doc.text(currentLine, rightBlockX, senderY);
              senderY += (line.size === 9) ? 4 : 4.5;
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          });
          
          // Render remaining text
          if (currentLine) {
            doc.text(currentLine, rightBlockX, senderY);
            senderY += (line.size === 9) ? 4.5 : 5.5;
          }
        } else {
          // Normal rendering
          doc.text(line.text, rightBlockX, senderY);
          senderY += (line.size === 9) ? 4.5 : 5.5;
        }
      });
      
      // Continue from whichever block is longer
      y = Math.max(recipientEndY, senderY) + 18;
      
      // === INVOICE TITLE ===
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(16, 185, 129); // Emerald green
      doc.text('RECHNUNG', marginLeft, y);
      doc.setTextColor(60);
      y += 12;
      
      // === INVOICE DETAILS (two columns - right column starts at rightBlockX) ===
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      
      // Row 1: Rechnungsnummer (left) + Rechnungsdatum (right, left-aligned at rightBlockX)
      doc.text('Rechnungsnummer:', marginLeft, y);
      doc.setFont(undefined, 'bold');
      doc.text(invoice.number, marginLeft + 38, y);
      doc.setFont(undefined, 'normal');
      
      // Rechnungsdatum left-aligned at rightBlockX (same start position as sender)
      doc.text(dateText, rightBlockX, y);
      y += 5;
      
      // Row 2: Leistungszeitraum (left) + Kundennummer (right, if exists)
      doc.text('Leistungszeitraum:', marginLeft, y);
      doc.text(getServicePeriodText(), marginLeft + 38, y);
      
      if (recipient.customerNumber) {
        const customerText = `Kundennummer: ${recipient.customerNumber}`;
        doc.text(customerText, rightBlockX, y);
      }
      y += 5;
      
      // Row 3 (optional): Bestellnummer
      if (invoice.poNumber) {
        doc.text('Bestellnummer:', marginLeft, y);
        doc.text(invoice.poNumber, marginLeft + 38, y);
        y += 5;
      }
      
      y += 12; // Space before table
      
      // === ITEMS TABLE ===
      const tableData = items.map((item, index) => {
        const price = parseFloat(item.price) || 0;
        const lineTotal = item.quantity * price;
        return [
          (index + 1).toString(),
          item.description,
          item.quantity.toString(),
          item.unit,
          formatCurrency(price, country),
          isKleinunternehmer ? '-' : `${item.taxRate}%`,
          formatCurrency(lineTotal, country),
        ];
      });
      
      // Table with balanced margins
      const tableLeftMargin = marginLeft;
      const tableRightMargin = marginRight;
      const availableWidth = pageWidth - tableLeftMargin - tableRightMargin; // 160mm
      
      autoTable(doc, {
        startY: y,
        head: [['Pos.', 'Beschreibung', 'Menge', 'Einheit', 'Preis', 'MwSt', 'Summe']],
        body: tableData,
        theme: 'plain',
        headStyles: { 
          fillColor: [16, 185, 129], 
          textColor: 255, 
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: 3,
          overflow: 'visible' // Prevent header text from wrapping
        },
        styles: { 
          fontSize: 9, 
          cellPadding: 3,
          overflow: 'linebreak',
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
          textColor: [60, 60, 60]
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250]
        },
        columnStyles: {
          // Total width: 160mm (contentWidth)
          0: { cellWidth: 12, halign: 'center' },   // Pos. (12mm)
          1: { cellWidth: 40 },                      // Beschreibung (40mm - reduced)
          2: { cellWidth: 18, halign: 'right' },    // Menge (18mm)
          3: { cellWidth: 20 },                      // Einheit (20mm)
          4: { cellWidth: 28, halign: 'right' },    // Preis (28mm - increased)
          5: { cellWidth: 14, halign: 'center' },   // MwSt (14mm)
          6: { cellWidth: 28, halign: 'right' },    // Summe (28mm - increased)
        },
        margin: { left: tableLeftMargin, right: tableRightMargin },
        tableWidth: availableWidth,
        showHead: 'firstPage',
        tableLineColor: [220, 220, 220],
        tableLineWidth: 0.1,
      });
      
      const tableEndY = doc.lastAutoTable.finalY;
      const tableRightX = pageWidth - tableRightMargin; // 185mm
      
      // === TOTALS BOX (right-aligned with table) ===
      y = tableEndY + 10;
      
      // Totals aligned with right edge of table
      const totalsLabelX = 125;
      const totalsValueX = tableRightX;
      let totalsY = y;
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(60);
      
      // Subtotal
      doc.text('Nettobetrag:', totalsLabelX, totalsY);
      doc.text(formatCurrency(totals.subtotal, country), totalsValueX, totalsY, { align: 'right' });
      totalsY += 6;
      
      // Tax lines
      if (!isKleinunternehmer) {
        Object.entries(totals.taxBreakdown).forEach(([rate, amount]) => {
          if (amount > 0) {
            doc.text(`MwSt ${rate}%:`, totalsLabelX, totalsY);
            doc.text(formatCurrency(amount, country), totalsValueX, totalsY, { align: 'right' });
            totalsY += 6;
          }
        });
      }
      
      // Separator line
      totalsY += 3;
      doc.setDrawColor(16, 185, 129);
      doc.setLineWidth(0.5);
      doc.line(totalsLabelX, totalsY, totalsValueX, totalsY);
      totalsY += 6;
      
      // Total
      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text('Gesamtbetrag:', totalsLabelX, totalsY);
      doc.setTextColor(16, 185, 129);
      doc.text(formatCurrency(totals.total, country), totalsValueX, totalsY, { align: 'right' });
      doc.setTextColor(0);
      
      // === KLEINUNTERNEHMER NOTICE ===
      y = tableEndY + 8;
      if (isKleinunternehmer) {
        doc.setFont(undefined, 'italic');
        doc.setFontSize(8);
        doc.setTextColor(100);
        const kleinText = KLEINUNTERNEHMER_TEXT[country];
        const splitText = doc.splitTextToSize(kleinText, totalsLabelX - marginLeft - 10);
        doc.text(splitText, marginLeft, y);
        doc.setTextColor(0);
        y += splitText.length * 4 + 5;
      }
      
      // Move y below the totals box
      y = Math.max(y, totalsY + 15);
      
      // === NOTES / CUSTOM TEXT ===
      if (notes && notes.trim()) {
        y += 5;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        const notesWidth = tableRightX - marginLeft;
        const splitNotes = doc.splitTextToSize(notes, notesWidth);
        doc.text(splitNotes, marginLeft, y);
        y += splitNotes.length * 4 + 10;
      }
      
      // === PAYMENT INFO ===
      y += 5;
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      doc.text('Bankverbindung', marginLeft, y);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      y += 6;
      
      if (sender.bank) {
        doc.text(`Bank: ${sender.bank}`, marginLeft, y);
        y += 4;
      }
      if (sender.iban) {
        doc.text(`IBAN: ${sender.iban}`, marginLeft, y);
        y += 4;
      }
      if (sender.bic) {
        doc.text(`BIC: ${sender.bic}`, marginLeft, y);
        y += 4;
      }
      
      // Due date
      if (invoice.dueDate) {
        y += 4;
        doc.setFont(undefined, 'bold');
        doc.text(`Zahlbar bis: ${invoice.dueDate}`, marginLeft, y);
      }
      
      // === SWISS QR-BILL SECTION (CH only) ===
      if (cfg.hasSwissQR && qrCodeBase64) {
        const qrSize = 46;
        const qrSectionHeight = qrSize + 14;
        const spaceNeeded = y + 10 + qrSectionHeight;
        const pageBottom = pageHeight - 10;
        if (spaceNeeded > pageBottom) {
          doc.addPage();
          y = marginTop;
        } else {
          y += 10;
        }
        doc.setFont(undefined, 'bold');
        doc.setFontSize(12);
        doc.setTextColor(16, 185, 129);
        doc.text('Swiss QR-Bill', marginLeft, y);
        doc.setTextColor(60);
        y += 10;
        try {
          doc.addImage(`data:image/png;base64,${qrCodeBase64}`, 'PNG', marginLeft, y, qrSize, qrSize);
          const qrInfoX = marginLeft + qrSize + 10;
          let qrInfoY = y + 5;
          doc.setFont(undefined, 'bold');
          doc.setFontSize(9);
          doc.text('Konto / Zahlbar an', qrInfoX, qrInfoY);
          qrInfoY += 5;
          doc.setFont(undefined, 'normal');
          doc.setFontSize(8);
          doc.text(senderData.iban || sender.iban, qrInfoX, qrInfoY);
          qrInfoY += 4;
          doc.text(senderData.name || sender.name, qrInfoX, qrInfoY);
          qrInfoY += 4;
          doc.text(`${senderData.street || sender.street} ${senderData.streetNumber || sender.streetNumber || ''}`.trim(), qrInfoX, qrInfoY);
          qrInfoY += 4;
          doc.text(`${senderData.zip || sender.zip} ${senderData.city || sender.city}`, qrInfoX, qrInfoY);
          qrInfoY += 8;
          doc.setFont(undefined, 'bold');
          doc.text('Zahlbar durch', qrInfoX, qrInfoY);
          qrInfoY += 5;
          doc.setFont(undefined, 'normal');
          doc.text(recipient.name, qrInfoX, qrInfoY);
          qrInfoY += 4;
          doc.text(`${recipient.street} ${recipient.streetNumber || ''}`.trim(), qrInfoX, qrInfoY);
          qrInfoY += 4;
          doc.text(`${recipient.zip} ${recipient.city}`, qrInfoX, qrInfoY);
          y += qrSize + 10;
        } catch (qrError) {
          console.error('QR code embedding error:', qrError);
        }
      }
      
      // Generate filename
      const fileName = `Rechnung_${invoice.number.replace(/[^a-zA-Z0-9-]/g, '_')}_${recipient.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      
      // Store PDF and filename for the conversion hub
      setGeneratedPDF(doc);
      setGeneratedFileName(fileName);
      
      // Track PDF generation
      trackCTAClick('invoice_pdf_generated', region);
      
      // Show conversion hub modal
      setShowModal(true);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Fehler beim Erstellen der PDF. Bitte versuche es erneut.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const toggleFaq = (index, question) => {
    setOpenFaq(openFaq === index ? null : index);
    if (openFaq !== index) trackFAQOpen(question);
  };
  
  // Sep 2026 (SEO-Paket 1): FAQs kommen länderspezifisch aus der Config — vorher hartcodierte DE-FAQs auf AT/CH (Klonfehler)
  const faqs = cfg.faqs || [];

  // FAQ JSON-LD Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white">
      {/* Structured Data: FAQ + SoftwareApp + Breadcrumb + HowTo + Organization */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <SoftwareApplicationSchema country={cfg.country} />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Rechnung erstellen", url: "/" },
        { name: cfg.breadcrumbLast }
      ]} />
      <HowToSchema
        name={cfg.howTo.name}
        description={cfg.howTo.description}
        totalTime={cfg.howTo.totalTime}
        steps={cfg.howTo.steps}
      />
      <OrganizationSchema />
      {/* Hero Section */}
      <section ref={heroRef} className="pt-8 pb-4 md:pt-12 md:pb-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <h1 className="font-display text-3xl md:text-5xl font-bold text-gray-900 tracking-tight mb-3">
              {cfg.heroTitle}
              <span className="text-emerald-600"> {cfg.heroTitleAccent}</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-4">
              {cfg.heroSubtitle}
            </p>
          </div>
          <TrustBadges cfg={cfg} />
        </div>
      </section>

      {/* Form Section */}
      <section ref={formRef} id="generator-form" className="pt-2 pb-24">
        <div className="max-w-4xl mx-auto px-4">
          
          {/* Progress */}
          <ProgressIndicator step={currentStep} />
          
          {/* Privacy Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">Deine Daten sind sicher</p>
              <p className="text-sm text-blue-600">Alle Eingaben werden nur in deinem Browser gespeichert und nie an unsere Server übertragen. 100% DSGVO-konform.</p>
            </div>
          </div>
          
          {/* Swiss QR-Bill Info (CH only) */}
          {cfg.hasSwissQR && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-8 flex items-start gap-3">
              <QrCode className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-800 font-medium">Swiss QR-Bill inklusive</p>
                <p className="text-sm text-emerald-600">Deine Rechnung enthält automatisch den QR-Code für einfaches Bezahlen.</p>
              </div>
            </div>
          )}
          
          {/* Sender Section */}
          <Card className="mb-6 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-emerald-600" />
                </div>
                Deine Daten (Rechnungssteller)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sender-name">Name / Firma *</Label>
                  <Input
                    id="sender-name"
                    value={sender.name}
                    onChange={(e) => handleSenderChange('name', e.target.value)}
                    placeholder="Max Mustermann"
                    className={`h-11 ${errors['sender.name'] ? 'border-red-500' : ''}`}
                  />
                  {errors['sender.name'] && <p className="text-red-500 text-xs mt-1">{errors['sender.name']}</p>}
                </div>
                <div>
                  <Label htmlFor="sender-email">E-Mail *</Label>
                  <Input
                    id="sender-email"
                    type="email"
                    value={sender.email}
                    onChange={(e) => handleSenderChange('email', e.target.value)}
                    placeholder="max@beispiel.de"
                    className={`h-11 ${errors['sender.email'] ? 'border-red-500' : ''}`}
                  />
                  {errors['sender.email'] && <p className="text-red-500 text-xs mt-1">{errors['sender.email']}</p>}
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="sender-street">Straße + Hausnummer *</Label>
                  <Input
                    id="sender-street"
                    value={sender.street}
                    onChange={(e) => handleSenderChange('street', e.target.value)}
                    placeholder="Musterstraße 123"
                    className={`h-11 ${errors['sender.street'] ? 'border-red-500' : ''}`}
                  />
                  {errors['sender.street'] && <p className="text-red-500 text-xs mt-1">{errors['sender.street']}</p>}
                </div>
                <div>
                  <Label htmlFor="sender-zip">PLZ *</Label>
                  <Input
                    id="sender-zip"
                    value={sender.zip}
                    onChange={(e) => handleSenderChange('zip', e.target.value)}
                    placeholder="12345"
                    className={`h-11 ${errors['sender.zip'] ? 'border-red-500' : ''}`}
                  />
                  {errors['sender.zip'] && <p className="text-red-500 text-xs mt-1">{errors['sender.zip']}</p>}
                </div>
                <div>
                  <Label htmlFor="sender-city">Ort *</Label>
                  <Input
                    id="sender-city"
                    value={sender.city}
                    onChange={(e) => handleSenderChange('city', e.target.value)}
                    placeholder="Berlin"
                    className={`h-11 ${errors['sender.city'] ? 'border-red-500' : ''}`}
                  />
                  {errors['sender.city'] && <p className="text-red-500 text-xs mt-1">{errors['sender.city']}</p>}
                </div>
                <div>
                  <Label htmlFor="sender-vatId">{TAX_ID_LABELS[country].vatId}</Label>
                  <Input
                    id="sender-vatId"
                    value={sender.vatId}
                    onChange={(e) => handleSenderChange('vatId', e.target.value)}
                    placeholder={TAX_ID_LABELS[country].vatIdPlaceholder}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="sender-taxNumber">{TAX_ID_LABELS[country].taxNumber}</Label>
                  <Input
                    id="sender-taxNumber"
                    value={sender.taxNumber}
                    onChange={(e) => handleSenderChange('taxNumber', e.target.value)}
                    placeholder={TAX_ID_LABELS[country].taxNumberPlaceholder}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="sender-iban">IBAN</Label>
                  <Input
                    id="sender-iban"
                    value={sender.iban}
                    onChange={(e) => handleSenderChange('iban', e.target.value)}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    className="h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="sender-bic">BIC</Label>
                  <Input
                    id="sender-bic"
                    value={sender.bic}
                    onChange={(e) => handleSenderChange('bic', e.target.value)}
                    placeholder="COBADEFFXXX"
                    className="h-11"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="sender-bank">Bank</Label>
                  <Input
                    id="sender-bank"
                    value={sender.bank}
                    onChange={(e) => handleSenderChange('bank', e.target.value)}
                    placeholder="Commerzbank AG"
                    className="h-11"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Recipient Section */}
          <Card className="mb-6 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <User className="h-4 w-4 text-emerald-600" />
                </div>
                Empfänger (Kunde)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="recipient-name">Name / Firma *</Label>
                  <Input
                    id="recipient-name"
                    value={recipient.name}
                    onChange={(e) => handleRecipientChange('name', e.target.value)}
                    placeholder="Musterfirma GmbH"
                    className={`h-11 ${errors['recipient.name'] ? 'border-red-500' : ''}`}
                  />
                  {errors['recipient.name'] && <p className="text-red-500 text-xs mt-1">{errors['recipient.name']}</p>}
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="recipient-street">Straße + Hausnummer *</Label>
                  <Input
                    id="recipient-street"
                    value={recipient.street}
                    onChange={(e) => handleRecipientChange('street', e.target.value)}
                    placeholder="Kundenweg 456"
                    className={`h-11 ${errors['recipient.street'] ? 'border-red-500' : ''}`}
                  />
                  {errors['recipient.street'] && <p className="text-red-500 text-xs mt-1">{errors['recipient.street']}</p>}
                </div>
                <div>
                  <Label htmlFor="recipient-zip">PLZ *</Label>
                  <Input
                    id="recipient-zip"
                    value={recipient.zip}
                    onChange={(e) => handleRecipientChange('zip', e.target.value)}
                    placeholder="54321"
                    className={`h-11 ${errors['recipient.zip'] ? 'border-red-500' : ''}`}
                  />
                  {errors['recipient.zip'] && <p className="text-red-500 text-xs mt-1">{errors['recipient.zip']}</p>}
                </div>
                <div>
                  <Label htmlFor="recipient-city">Ort *</Label>
                  <Input
                    id="recipient-city"
                    value={recipient.city}
                    onChange={(e) => handleRecipientChange('city', e.target.value)}
                    placeholder="München"
                    className={`h-11 ${errors['recipient.city'] ? 'border-red-500' : ''}`}
                  />
                  {errors['recipient.city'] && <p className="text-red-500 text-xs mt-1">{errors['recipient.city']}</p>}
                </div>
                <div>
                  <Label htmlFor="recipient-vatId">USt-IdNr. (optional)</Label>
                  <Input
                    id="recipient-vatId"
                    value={recipient.vatId}
                    onChange={(e) => handleRecipientChange('vatId', e.target.value)}
                    placeholder={TAX_ID_LABELS[country].vatIdPlaceholder}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="recipient-customerNumber">Kundennummer (optional)</Label>
                  <Input
                    id="recipient-customerNumber"
                    value={recipient.customerNumber}
                    onChange={(e) => handleRecipientChange('customerNumber', e.target.value)}
                    placeholder="K-001"
                    className="h-11"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Feature Highlight after recipient */}
          <FeatureHighlight 
            icon={Users}
            title="Kunden speichern & wiederverwenden"
            description="Mit ivoliq speicherst du Kundendaten einmal und füllst sie automatisch in neue Rechnungen ein."
            cta="Mehr erfahren"
            region={region}
          />
          
          {/* Invoice Details Section */}
          <Card className="mb-6 mt-6 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Receipt className="h-4 w-4 text-emerald-600" />
                </div>
                Rechnungsdetails
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="invoice-number">Rechnungsnummer *</Label>
                  <Input
                    id="invoice-number"
                    value={invoice.number}
                    onChange={(e) => handleInvoiceChange('number', e.target.value)}
                    placeholder="2025-001"
                    className={`h-11 ${errors['invoice.number'] ? 'border-red-500' : ''}`}
                  />
                  {errors['invoice.number'] && <p className="text-red-500 text-xs mt-1">{errors['invoice.number']}</p>}
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Achte auf einen lückenlosen Nummernkreis!
                  </p>
                </div>
                <div>
                  <Label htmlFor="invoice-date">Rechnungsdatum *</Label>
                  <Input
                    id="invoice-date"
                    type="date"
                    value={invoice.date}
                    onChange={(e) => handleInvoiceChange('date', e.target.value)}
                    className={`h-11 ${errors['invoice.date'] ? 'border-red-500' : ''}`}
                  />
                  {errors['invoice.date'] && <p className="text-red-500 text-xs mt-1">{errors['invoice.date']}</p>}
                </div>
                <div>
                  <Label htmlFor="invoice-serviceStartDate">Leistungsdatum (Start) *</Label>
                  <Input
                    id="invoice-serviceStartDate"
                    type="date"
                    value={invoice.serviceStartDate}
                    onChange={(e) => handleInvoiceChange('serviceStartDate', e.target.value)}
                    max={invoice.date}
                    className={`h-11 ${errors['invoice.serviceStartDate'] ? 'border-red-500' : ''}`}
                  />
                  {errors['invoice.serviceStartDate'] && <p className="text-red-500 text-xs mt-1">{errors['invoice.serviceStartDate']}</p>}
                </div>
                <div>
                  <Label htmlFor="invoice-serviceEndDate">Leistungsdatum (Ende) <span className="text-gray-400">(optional)</span></Label>
                  <Input
                    id="invoice-serviceEndDate"
                    type="date"
                    value={invoice.serviceEndDate}
                    onChange={(e) => handleInvoiceChange('serviceEndDate', e.target.value)}
                    min={invoice.serviceStartDate}
                    max={invoice.date}
                    className="h-11"
                  />
                  <p className="text-xs text-gray-500 mt-1">Für Zeiträume ausfüllen</p>
                </div>
                <div>
                  <Label htmlFor="invoice-dueDate">Zahlungsziel (optional)</Label>
                  <Input
                    id="invoice-dueDate"
                    value={invoice.dueDate}
                    onChange={(e) => handleInvoiceChange('dueDate', e.target.value)}
                    placeholder="14 Tage oder 31.12.2025"
                    className="h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="invoice-poNumber">Bestellnummer / PO (optional)</Label>
                  <Input
                    id="invoice-poNumber"
                    value={invoice.poNumber}
                    onChange={(e) => handleInvoiceChange('poNumber', e.target.value)}
                    placeholder="PO-12345"
                    className="h-11"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Items Section */}
          <Card className="mb-6 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lg">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-4 w-4 text-emerald-600" />
                  </div>
                  Positionen
                </div>
                <Button variant="outline" size="sm" onClick={addItem} className="h-9">
                  <Plus className="h-4 w-4 mr-1" /> Position
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Table Header - Using percentage widths for better control */}
              <div className="hidden md:flex gap-3 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                <div className="flex-[3]">Beschreibung</div>
                <div className="w-24 text-center">Menge</div>
                <div className="w-28">Einheit</div>
                <div className="w-28 text-right">Preis (netto)</div>
                <div className="w-20 text-center">MwSt</div>
                <div className="w-10"></div>
              </div>
              
              {items.map((item, index) => (
                <div key={item.id} className="border rounded-lg p-4 bg-gray-50/50">
                  <div className="flex flex-col md:flex-row gap-3 items-start">
                    {/* Description - flexible width */}
                    <div className="w-full md:flex-[3]">
                      <Label className="md:hidden text-xs">Beschreibung *</Label>
                      <Textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                        placeholder="Webdesign, Beratung, etc."
                        rows={2}
                        className={`resize-none ${errors[`items.${index}.description`] ? 'border-red-500' : ''}`}
                      />
                      {errors[`items.${index}.description`] && <p className="text-red-500 text-xs mt-1">{errors[`items.${index}.description`]}</p>}
                    </div>
                    
                    {/* Quantity - fixed width for 3+ digits */}
                    <div className="w-full md:w-24">
                      <Label className="md:hidden text-xs">Menge</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-10 text-center"
                      />
                    </div>
                    
                    {/* Unit - fixed width */}
                    <div className="w-full md:w-28">
                      <Label className="md:hidden text-xs">Einheit</Label>
                      <Select value={item.unit} onValueChange={(v) => handleItemChange(item.id, 'unit', v)}>
                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Price - fixed width */}
                    <div className="w-full md:w-28">
                      <Label className="md:hidden text-xs">Preis (netto) *</Label>
                      <div className="relative">
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={item.price}
                          onChange={(e) => handlePriceChange(item.id, e.target.value)}
                          placeholder="0,00"
                          className={`h-10 text-right pr-7 ${errors[`items.${index}.price`] ? 'border-red-500' : ''}`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                      </div>
                      {errors[`items.${index}.price`] && <p className="text-red-500 text-xs mt-1">{errors[`items.${index}.price`]}</p>}
                    </div>
                    
                    {/* Tax Rate - fixed width for "19%" */}
                    <div className="w-full md:w-20">
                      <Label className="md:hidden text-xs">MwSt</Label>
                      <Select 
                        value={item.taxRate.toString()} 
                        onValueChange={(v) => handleItemChange(item.id, 'taxRate', parseFloat(v))}
                        disabled={isKleinunternehmer}
                      >
                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TAX_RATES[country].map(r => <SelectItem key={r.value} value={r.value.toString()}>{r.value}%</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Delete button - fixed width */}
                    <div className="w-full md:w-10 flex items-center justify-end">
                      {items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="h-10 w-10 p-0">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile sum display */}
                  <div className="md:hidden mt-3 pt-3 border-t flex justify-between items-center">
                    <span className="text-sm text-gray-600">Summe:</span>
                    <span className="text-lg font-semibold text-emerald-600">
                      {formatCurrency(item.quantity * (parseFloat(item.price) || 0), country)}
                    </span>
                  </div>
                </div>
              ))}
              
              {items.length >= 5 && (
                <FeatureHighlight 
                  icon={Sparkles}
                  title="Positionen als Vorlagen speichern"
                  description="Spare Zeit! Mit ivoliq speicherst du häufig verwendete Positionen und fügst sie mit einem Klick ein."
                  cta="Vorlagen entdecken"
                  region={region}
                />
              )}
            </CardContent>
          </Card>
          
          {/* Totals & Options */}
          <Card className="mb-6 shadow-sm">
            <CardContent className="pt-6">
              {/* Notes/Custom Text */}
              <div className="mb-6">
                <Label htmlFor="notes" className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  Hinweistext / Zahlungsbedingungen (optional)
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="z.B. Zahlungsziel: 14 Tage netto&#10;oder: Vielen Dank für Ihren Auftrag!"
                  className="min-h-[80px] resize-y"
                  maxLength={500}
                />
                <p className="text-xs text-gray-400 mt-1">{notes.length}/500 Zeichen</p>
              </div>
              
              {/* Kleinunternehmer checkbox */}
              <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl border">
                <Checkbox 
                  id="kleinunternehmer" 
                  checked={isKleinunternehmer}
                  onCheckedChange={setIsKleinunternehmer}
                  className="h-5 w-5"
                />
                <div>
                  <Label htmlFor="kleinunternehmer" className="cursor-pointer font-medium">
                    Ich bin Kleinunternehmer (keine USt ausweisen)
                  </Label>
                  <p className="text-xs text-gray-500 mt-0.5">Gemäß § 19 UStG wird dann keine Umsatzsteuer berechnet</p>
                </div>
              </div>
              
              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-full md:w-80 space-y-3 bg-gray-50 rounded-xl p-4">
                  <div className="flex justify-between text-gray-600">
                    <span>Zwischensumme (netto)</span>
                    <span className="font-medium">{formatCurrency(totals.subtotal, country)}</span>
                  </div>
                  {!isKleinunternehmer && Object.entries(totals.taxBreakdown).map(([rate, amount]) => (
                    amount > 0 && (
                      <div key={rate} className="flex justify-between text-gray-600">
                        <span>MwSt {rate}%</span>
                        <span>{formatCurrency(amount, country)}</span>
                      </div>
                    )
                  ))}
                  <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-300">
                    <span>Gesamtbetrag</span>
                    <span className="text-emerald-600">{formatCurrency(totals.total, country)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Generate Button */}
          <div className="text-center hidden md:block">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-7 text-lg shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
              onClick={generatePDF}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  PDF wird erstellt...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-2" />
                  Rechnung erstellen
                </>
              )}
            </Button>
            <p className="text-sm text-gray-500 mt-4 max-w-md mx-auto">
              Professionelle PDF-Rechnung • Kostenlos • Kein Account nötig
            </p>
          </div>
        </div>
      </section>
      
      {/* Section A: Warum dieser Rechnungsgenerator? */}
      <section ref={warumRef} className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">
            Warum dieser Rechnungsgenerator?
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {cfg.warumFeatures.map((feat, i) => {
              const IconComp = { Shield, Check, Star, Euro, Download, Calculator }[feat.icon] || Check;
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComp className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{feat.title}</h3>
                    <p className="text-sm text-gray-600">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-sm text-amber-800">
              <strong>💡 Tipp:</strong> Mit ivoliq kannst du Rechnungen speichern, verwalten und automatisch versenden – 
              <Link to="/signup" className="text-amber-700 font-semibold hover:underline"> kostenlos registrieren</Link>
            </p>
          </div>
        </div>
      </section>
      
      {/* Section B: Pflichtangaben auf deutschen Rechnungen */}
      <section ref={pflichtangabenRef} className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">
            {cfg.pflichtTitle}
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
            {cfg.pflichtIntro}
          </p>
          
          <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <ol className="space-y-4">
              {cfg.pflichtItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </span>
                  <span className="text-gray-700 pt-0.5">{item}</span>
                </li>
              ))}
            </ol>
          </div>
          
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              <strong>Wichtig:</strong> {cfg.pflichtNote}
            </p>
          </div>
        </div>
      </section>
      
      {/* Section C: Von kostenlos zu professionell */}
      <section ref={upgradeRef} className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">
            Von kostenlos zu professionell: ivoliq
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
            Unser Rechnungsgenerator ist perfekt für einzelne Rechnungen. Aber als Freelancer brauchst du mehr: 
            Rechnungsverwaltung, automatischen Versand, Zahlungsstatus und Finanzübersicht.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                  <Save className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Rechnungen speichern & verwalten</h3>
                <p className="text-sm text-gray-600">Alle Rechnungen an einem Ort, jederzeit abrufbar und archiviert.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Automatischer E-Mail-Versand</h3>
                <p className="text-sm text-gray-600">Rechnungen direkt per E-Mail versenden mit Lesebestätigung.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Zahlungsstatus tracken</h3>
                <p className="text-sm text-gray-600">Sieh sofort, welche Rechnungen bezahlt, offen oder überfällig sind.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                  <Calculator className="h-5 w-5 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Finanzübersicht & Steuerberechnung</h3>
                <p className="text-sm text-gray-600">Einnahmen, Ausgaben und Steuerrücklagen auf einen Blick.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-3">
                  <Clock className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Zeiterfassung integriert</h3>
                <p className="text-sm text-gray-600">Timer starten → Zeit erfassen → Rechnung mit einem Klick erstellen.</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                  <Globe className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">DACH-Compliance</h3>
                <p className="text-sm text-gray-600">DE/AT/CH in einer Lösung, inkl. Swiss QR-Bill für die Schweiz.</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center">
            <Link to={`/signup?ref=invoice-generator-${region}-upgrade`} onClick={() => trackCTAClick('upgrade', 'Jetzt kostenlos upgraden')}>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-7 text-lg shadow-lg">
                Jetzt kostenlos upgraden
              </Button>
            </Link>
            <p className="text-sm text-gray-500 mt-4">
              ✓ Free Plan ohne Kreditkarte • ✓ 30 Tage Pro kostenlos
            </p>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section ref={faqRef} className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-4">
            {cfg.faqTitle}
          </h2>
          <p className="text-center text-gray-600 mb-10">{cfg.faqSubtitle}</p>
          
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border rounded-xl overflow-hidden">
                <button
                  style={{ display: "flex" }} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  onClick={() => toggleFaq(index, faq.q)}
                >
                  <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-gray-600 bg-gray-50">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Final CTA Section */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Mehr als nur ein Rechnungsgenerator
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Verwalte alle Rechnungen mit ivoliq – von der Zeiterfassung zur Rechnung mit einem Klick. Kostenlos starten.
          </p>
          <Link to={`/signup?ref=invoice-generator-${region}-final-cta`} onClick={() => trackCTAClick('final_cta', 'Jetzt kostenlos starten')}>
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 px-10 py-7 text-lg shadow-2xl">
              Jetzt kostenlos starten
            </Button>
          </Link>
          <p className="text-sm text-emerald-100 mt-6">
            ✓ Free Plan ohne Kreditkarte • ✓ 30 Tage Pro kostenlos testen
          </p>
          
          {/* Trust Block */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-emerald-100">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <span>DSGVO-konform</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>Für Freelancer in DE, AT & CH gebaut</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4" />
              <span>Rechtssicher für DE, AT & CH</span>
            </div>
          </div>
        </div>
      </section>
      
      {cfg.hasSwissQR && (
        <section className="py-10 bg-emerald-50 border-y border-emerald-100" data-testid="qr-generator-banner">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Nur einen QR-Einzahlungsschein benötigt?</h2>
              <p className="text-sm text-gray-600">Zahlteil + Empfangsschein nach SIX-Vorgaben in 2 Minuten — ohne komplette Rechnung.</p>
            </div>
            <Link to="/qr-rechnung-erstellen-kostenlos" data-testid="qr-generator-link" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 whitespace-nowrap">
              QR-Rechnung kostenlos erstellen
            </Link>
          </div>
        </section>
      )}
      <SeoClusterLinks cluster="rechnung" country={country} />
      <Footer />
      
      {/* Floating CTA for Mobile */}
      <FloatingCTA onGenerate={generatePDF} />
      
      {/* Conversion Hub Modal */}
      <ConversionHubModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        region={region}
        pdfDoc={generatedPDF}
        fileName={generatedFileName}
        trackEvent={trackCTAClick}
        invoiceData={{
          sender: sender,
          recipient: recipient,
          invoice: invoice,
          items: items,
          totals: totals,
          isKleinunternehmer: isKleinunternehmer
        }}
        onSaveAndSignup={handleSaveAndSignup}
      />
      
      {/* Bottom padding for mobile floating CTA */}
      <div className="h-20 md:hidden" />
    </div>
  );
}
